<template>
  <a-modal
    :visible="visible"
    :title="i18n('email_verify.title')"
    wrapClassName="email-verify-modal-wrap"
    :closable="false"
    :maskClosable="false"
    :confirm-loading="submitting"
    :footer="null"
    @cancel="() => {}"
  >
    <a-form layout="vertical" :model="form">
      <a-form-item :label="i18n('email_verify.label_email')" required>
        <a-input
          v-model:value="form.email"
          type="email"
          :placeholder="i18n('email_verify.placeholder_email')"
          :disabled="submitting"
          allow-clear
        />
        <div v-if="emailHint" class="email-hint">{{ emailHint }}</div>
      </a-form-item>
      <a-form-item :label="i18n('email_verify.label_code')" required>
        <div class="code-input-group">
          <a-input
            v-model:value="form.code"
            :placeholder="i18n('email_verify.placeholder_code')"
            :maxlength="6"
            :disabled="submitting"
            allow-clear
            class="code-input-inner"
          />
          <a-button
            type="link"
            class="code-btn"
            :disabled="sendCodeDisabled"
            @click="onSendCode"
          >
            {{ sendCodeButtonText }}
          </a-button>
        </div>
      </a-form-item>
      <a-alert
        v-if="errorMsgHtml"
        type="error"
        show-icon
        style="margin-bottom: 16px"
        class="email-verify-notice-alert"
      >
        <template #message>{{ errorMsgTitle }}</template>
        <template #description>
          <div class="email-verify-notice-html" v-html="errorMsgHtml"></div>
        </template>
      </a-alert>
      <a-alert
        v-else-if="errorMsg"
        type="error"
        :message="errorMsg"
        show-icon
        style="margin-bottom: 16px"
      />
      <div class="modal-footer">
        <a-button type="primary" :loading="submitting" @click="onSubmit">
          {{ i18n('email_verify.btn_confirm') }}
        </a-button>
      </div>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { message, notification } from 'ant-design-vue'
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from '../../store'
import { getError } from '../common'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
}>()

const { t: i18n } = useI18n()
const store = useStore()
const form = ref({ email: '', code: '' })
const submitting = ref(false)
const errorMsg = ref('')
/** Account System Notice 时后端返回的 HTML 内容，用于 v-html 展示 */
const errorMsgHtml = ref('')
const errorMsgTitle = ref('')
const countdown = ref(0)
let countdownTimer: ReturnType<typeof setInterval> | null = null

const sendCodeDisabled = computed(() => submitting.value || countdown.value > 0)
const sendCodeButtonText = computed(() =>
  countdown.value > 0 ? i18n('email_verify.resend_in', { n: countdown.value }) : i18n('email_verify.btn_send_code')
)

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
function isEmailValid(email: string) {
  return EMAIL_REGEX.test((email || '').trim())
}
const emailHint = computed(() => {
  const email = form.value.email?.trim()
  if (!email) return ''
  return isEmailValid(email) ? '' : i18n('email_verify.hint_invalid_email')
})

function startCountdown() {
  countdown.value = 60
  if (countdownTimer) clearInterval(countdownTimer)
  countdownTimer = setInterval(() => {
    if (countdown.value <= 1) {
      countdown.value = 0
      if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    } else {
      countdown.value -= 1
    }
  }, 1000)
}

async function onSendCode() {
  const email = form.value.email?.trim()
  if (!email) {
    errorMsg.value = i18n('email_verify.err_enter_email_first')
    return
  }
  if (!isEmailValid(email)) {
    errorMsg.value = i18n('email_verify.hint_invalid_email')
    return
  }
  errorMsg.value = ''
  try {
    const { data } = await store.dispatch('request', {
      method: 'POST',
      endpoint: 'authenticate/verification_code',
      data: {
        email: {
          to: email,
          template_name: 'VERIFICATION_CODE',
          replacements: {},
        },
      },
    })
    if (data?.send === true) {
      message.success(i18n('email_verify.success_sent'))
      startCountdown()
    } else {
      errorMsg.value = i18n('email_verify.err_send_failed')
    }
  } catch (err: any) {
    notification.open(getError(err))
  }
}

async function onSubmit() {
  const email = form.value.email?.trim()
  const code = form.value.code?.trim()
  if (!email) {
    errorMsg.value = i18n('email_verify.err_enter_email')
    return
  }
  if (!isEmailValid(email)) {
    errorMsg.value = i18n('email_verify.hint_invalid_email')
    return
  }
  if (!code) {
    errorMsg.value = i18n('email_verify.err_enter_code')
    return
  }
  errorMsg.value = ''
  errorMsgHtml.value = ''
  errorMsgTitle.value = ''
  submitting.value = true
  try {
    await store.dispatch('request', {
      method: 'PATCH',
      endpoint: 'authenticate/email',
      data: { email, code },
    })
    message.success(i18n('email_verify.success_verified'))
    emit('success')
  } catch (err: any) {
    const res = err?.response || err
    const data = res?.data || {}
    const name = data.name
    const msg = data.message || res?.message || i18n('email_verify.err_verify_failed')
    if (name === 'Account System Notice' && data.message) {
      errorMsgHtml.value = data.message
      errorMsgTitle.value = name
      errorMsg.value = ''
    } else {
      errorMsgHtml.value = ''
      errorMsgTitle.value = ''
      errorMsg.value = msg
      notification.open(getError(err))
    }
  } finally {
    submitting.value = false
  }
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      const sourceEmail = store.getters.zpSource?.Email
      form.value = {
        email: (typeof sourceEmail === 'string' && sourceEmail.trim()) ? sourceEmail.trim() : '',
        code: ''
      }
      errorMsg.value = ''
      errorMsgHtml.value = ''
      errorMsgTitle.value = ''
    }
  }
)

onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
})
</script>

<style scoped lang="less">
.email-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #ff4d4f;
}
.code-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  transition: border-color 0.2s;
  overflow: hidden;
  &:focus-within {
    border-color: #4096ff;
    box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
  }
  /* 输入区域：去掉自身边框，只保留外层 .code-input-group 的边框 */
  .code-input-inner {
    flex: 1;
    min-width: 0;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
  /* 去掉内层所有边框：a-input 根节点可能带 ant-input-affix-wrapper，用 :deep 统一清掉 */
  .code-input-inner,
  .code-input-group > *:first-child,
  .code-input-group :deep(.ant-input-affix-wrapper),
  .code-input-inner :deep(.ant-input-affix-wrapper),
  .code-input-inner :deep(.ant-input-wrapper),
  .code-input-inner :deep(.ant-input),
  .code-input-inner :deep(input) {
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    outline: none !important;
  }
  .code-input-inner :deep(.ant-input-affix-wrapper),
  .code-input-inner :deep(.ant-input-wrapper),
  .code-input-inner :deep(.ant-input),
  .code-input-inner :deep(input) {
    background: transparent !important;
  }
  .code-input-inner:focus-within :deep(.ant-input-affix-wrapper),
  .code-input-inner:focus-within :deep(.ant-input-wrapper),
  .code-input-inner:focus-within :deep(.ant-input),
  .code-input-inner:focus-within :deep(input) {
    box-shadow: none !important;
    border-color: transparent !important;
  }
  .code-btn {
    flex-shrink: 0;
    padding: 0 12px;
    border-radius: 0;
  }
}
.modal-footer {
  margin-top: 8px;
  text-align: right;
}
.email-verify-notice-html {
  line-height: 1.6;
  word-break: break-word;
  :deep(p) {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  :deep(a) {
    color: #1890ff;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  :deep(b) {
    font-weight: 600;
  }
}
</style>
<!-- 弹窗可能挂载到 body，用非 scoped 保证内层输入框边框被去掉；16px 避免 iOS 等聚焦时自动放大且不还原 -->
<style lang="less">
.email-verify-modal-wrap {
  .ant-input,
  .ant-input-affix-wrapper input,
  input {
    font-size: 16px !important;
  }
}
.email-verify-modal-wrap .code-input-group .ant-input-affix-wrapper,
.email-verify-modal-wrap .code-input-group .ant-input,
.email-verify-modal-wrap .code-input-group input {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}
.email-verify-modal-wrap .code-input-group .ant-input-affix-wrapper:focus,
.email-verify-modal-wrap .code-input-group .ant-input-affix-wrapper:hover {
  border: none !important;
  box-shadow: none !important;
}
</style>
